// Barge-In (OpenAI Realtime) WebRTC helper for INZARA Voice.
// IMPORTANT: this is LISTEN-ONLY. The Realtime model is used purely for
// low-latency mic streaming, input transcription, and voice-activity detection
// (so the user can barge in). It NEVER generates its own spoken answer — that
// would hallucinate insurance facts. Instead the caller runs the transcribed
// question through AI Explore and speaks the EXACT result via our own TTS.
import { api } from '@/lib/api';

export default class RealtimeVoice {
  constructor({ onUserTranscript, onStatus, onSpeaking, onBargeIn } = {}) {
    this.pc = null;
    this.dc = null;
    this.audioEl = null;
    this.stream = null;
    this.connected = false;
    this.rebind({ onUserTranscript, onStatus, onSpeaking, onBargeIn });
  }

  rebind({ onUserTranscript, onStatus, onSpeaking, onBargeIn } = {}) {
    if (onUserTranscript) this.onUserTranscript = onUserTranscript;
    if (onStatus) this.onStatus = onStatus;
    if (onSpeaking) this.onSpeaking = onSpeaking;
    if (onBargeIn) this.onBargeIn = onBargeIn;
    this.onUserTranscript = this.onUserTranscript || (() => {});
    this.onStatus = this.onStatus || (() => {});
    this.onSpeaking = this.onSpeaking || (() => {});
    this.onBargeIn = this.onBargeIn || (() => {});
  }

  async start() {
    this.onStatus('connecting');
    // Backend mints a short-lived ephemeral key (ek_...) with the tenant/env key.
    const sess = await api.voiceRealtimeSession();
    const ek = sess.client_secret;
    const model = sess.model || 'gpt-realtime';
    if (!ek) throw new Error('Could not start Barge-In (no session token)');

    this.pc = new RTCPeerConnection();

    // Remote audio (INZARA's voice).
    this.audioEl = document.createElement('audio');
    this.audioEl.autoplay = true;
    this.pc.ontrack = (e) => { this.audioEl.srcObject = e.streams[0]; };

    // Local mic.
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.stream.getTracks().forEach((t) => this.pc.addTrack(t, this.stream));

    // Events channel.
    this.dc = this.pc.createDataChannel('oai-events');
    this.dc.onopen = () => {
      // LISTEN-ONLY session: transcribe the mic and detect turns, but tell the
      // model NEVER to auto-generate a spoken response (create_response:false).
      // We speak AI Explore's exact answer ourselves via TTS.
      this.dc.send(JSON.stringify({
        type: 'session.update',
        session: {
          type: 'realtime',
          audio: {
            input: {
              transcription: { model: 'whisper-1' },
              turn_detection: { type: 'semantic_vad', eagerness: 'medium', create_response: false },
            },
          },
        },
      }));
      this.onStatus('live');
    };
    this.dc.onmessage = (e) => this._onEvent(e.data);

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);

    // Browser negotiates WebRTC SDP directly with OpenAI using the ephemeral key.
    const resp = await fetch(`https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(model)}`, {
      method: 'POST',
      body: offer.sdp,
      headers: { Authorization: `Bearer ${ek}`, 'Content-Type': 'application/sdp' },
    });
    if (!resp.ok) throw new Error(`Realtime negotiation failed (${resp.status})`);
    const answerSdp = await resp.text();
    await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    this.connected = true;
  }

  _onEvent(raw) {
    let ev;
    try { ev = JSON.parse(raw); } catch (_) { return; }
    switch (ev.type) {
      case 'input_audio_buffer.speech_started':
        // User started talking → barge in (stop our TTS playback).
        this.onBargeIn();
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (ev.transcript) this.onUserTranscript(ev.transcript.trim());
        break;
      default:
        break;
    }
  }

  stop() {
    this.connected = false;
    try { this.dc?.close(); } catch (_) { /* noop */ }
    try { this.pc?.close(); } catch (_) { /* noop */ }
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    if (this.audioEl) { try { this.audioEl.srcObject = null; } catch (_) { /* noop */ } }
    this.pc = null; this.dc = null; this.stream = null;
    this.onStatus('idle');
    this.onSpeaking(false);
  }
}
